import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box, Card, CardContent, Typography, Button, TextField, Chip, Stack,
  LinearProgress, Alert, Divider, ToggleButton, ToggleButtonGroup,
} from '@mui/material'
import { CheckCircle, ArrowForward, ArrowBack } from '@mui/icons-material'
import { demoApi } from '../services/api'

/**
 * Request Demo — public, unauthenticated.
 *
 * Two things make this different from a normal lead form.
 *
 * 1. Nothing is lost if the visitor stops. Answers are written to
 *    localStorage on every change, and the non-personal ones are also saved
 *    server-side against an opaque draft token. Contact details are held back
 *    until submit — persisting a half-typed email address because someone
 *    paused is not something to do quietly.
 *
 * 2. Each answer buys something. The panel on the right shows real knowledge
 *    base coverage for the stack they select, and the demo agenda they are
 *    building. The numbers come from the KB, live. Nothing here simulates a
 *    finding, a score or a scan result — we say what ARIAS would look for,
 *    never what it "found".
 */

const STORAGE_KEY = 'arias.demo.draft.v1'

const FRAMEWORKS = [
  { id: 'langchain', label: 'LangChain' },
  { id: 'langgraph', label: 'LangGraph' },
  { id: 'crewai', label: 'CrewAI' },
  { id: 'autogen', label: 'AutoGen' },
  { id: 'semantic_kernel', label: 'Semantic Kernel' },
  { id: 'llamaindex', label: 'LlamaIndex' },
  { id: 'bedrock', label: 'AWS Bedrock' },
  { id: 'azure_ai', label: 'Azure AI' },
  { id: 'google_vertex', label: 'Google Vertex' },
  { id: 'openai', label: 'OpenAI Agents' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'dspy', label: 'DSPy' },
  { id: 'haystack', label: 'Haystack' },
  { id: 'smolagents', label: 'Smolagents' },
  { id: 'mcp', label: 'MCP' },
  { id: 'other', label: 'Something else' },
]

// One true, specific thing ARIAS looks for in each stack. Capability
// statements, not findings — nothing here claims to know anything about the
// visitor's code.
const FRAMEWORK_INSIGHT: Record<string, string> = {
  crewai: 'Agents declared in agents.yaml are inventoried alongside code-defined ones',
  langgraph: 'Graph edges are read as delegation paths, not just control flow',
  langchain: 'Tool permissions are resolved through the executor, not just the constructor',
  autogen: 'Group chat managers are mapped as orchestrators with their own blast surface',
  mcp: 'Both sides are detected — agents that host tools and agents that consume them',
  bedrock: 'Runtime platform is tracked separately from framework',
  azure_ai: 'Runtime platform is tracked separately from framework',
  google_vertex: 'Runtime platform is tracked separately from framework',
}

// Deliberately worded so no option reads as a finish line. Agentic adoption
// is a programme that keeps moving — teams keep adding use cases, rewording
// prompts and swapping models long after the first agent ships. A ladder
// ending at "scaled" would frame ARIAS as a one-time assessment, which is the
// opposite of what it is.
const ADOPTION = [
  { id: 'exploring', label: 'Building our first agents' },
  { id: 'piloting', label: 'Piloting with real users' },
  { id: 'production', label: 'In production, still building' },
  { id: 'scaling', label: 'Multiple teams shipping continuously' },
]

// How often the estate changes. The most direct predictor of whether drift
// detection is worth anything: an estate that changes weekly accumulates
// drift a quarterly one never will.
const CADENCE = [
  { id: 'weekly', label: 'Weekly or faster' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'ad_hoc', label: 'No set rhythm' },
  { id: 'unsure', label: 'Not sure' },
]

const USE_CASE_BANDS = ['1-2', '3-5', '6-15', '16+', 'unsure']

// Statements about how drift accumulates, not claims about the visitor's
// agents — we have not seen them. Each one is true of any estate changing at
// that rate, which is what makes it safe to say before a single scan.
const CADENCE_NOTE: Record<string, string> = {
  weekly: 'At weekly cadence an agent can move a long way in a quarter without any single change looking remarkable in review.',
  monthly: 'Monthly changes are the hardest to catch by eye — far enough apart that the last review feels recent, often enough to compound.',
  quarterly: 'Quarterly releases make each change larger, so what shifted between two approved versions is worth establishing precisely.',
  ad_hoc: 'Without a set rhythm, the gap between what was approved and what is running is the thing nobody is tracking.',
}

const HOSTING = [
  { id: 'cloud', label: 'Cloud' },
  { id: 'on_prem', label: 'On-premise' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'undecided', label: 'Not decided' },
]

const BANDS = ['1-5', '6-20', '21-50', '51-200', '200+', 'unsure']

// Two audiences buy this for different reasons and neither should have to
// read the other's list to find themselves. Engineering asks "what breaks and
// when"; security and risk ask "can I prove the control held". Same mechanism,
// different question — so the chips are grouped rather than pooled.
const FEATURE_GROUPS = [
  {
    group: 'Engineering',
    items: [
      { id: 'inventory', label: 'Agent inventory',
        agenda: 'Finding every agent, including the ones defined in spec files rather than code' },
      { id: 'drift', label: 'Drift between commits',
        agenda: 'How a baseline is set, and what happens at the commit that moves an agent off it' },
      { id: 'gate', label: 'CI/CD release gate',
        agenda: 'Block mode and monitor mode, and choosing per pipeline' },
      { id: 'topology', label: 'Topology & dependencies',
        agenda: 'What each agent can reach, and what it delegates to' },
      { id: 'cost', label: 'Cost estimation',
        agenda: 'Token and cost projection before anything runs' },
    ],
  },
  {
    group: 'Security, risk & compliance',
    items: [
      { id: 'attestation', label: 'Proving what was approved',
        agenda: 'Recording which version was approved, and enforcing expiry rather than tracking it' },
      { id: 'decision_rights', label: 'Who decides what stops a release',
        agenda: 'Release policy written down, applied uniformly, evaluated automatically — you hold the switch' },
      { id: 'compliance', label: 'Audit evidence for your frameworks',
        agenda: 'Evidence produced by the system under control, not reconstructed afterwards' },
      { id: 'grc', label: 'Feeding your existing GRC platform',
        agenda: 'How evidence reaches the platform that already holds your controls' },
      { id: 'residency', label: 'Data residency & blast radius',
        agenda: 'Where analysis runs, what leaves your environment, and why a bad ARIAS day is a build result' },
    ],
  },
]

const FEATURES = FEATURE_GROUPS.flatMap(g => g.items)

type Draft = {
  frameworks: string[]
  adoption_stage: string
  hosting: string
  agent_count_band: string
  use_case_count_band: string
  release_cadence: string
  interested_features: string[]
  name: string
  email: string
  company: string
  job_title: string
  notes: string
}

const EMPTY: Draft = {
  frameworks: [], adoption_stage: '', hosting: '', agent_count_band: '',
  use_case_count_band: '', release_cadence: '',
  interested_features: [], name: '', email: '', company: '', job_title: '', notes: '',
}

export default function RequestDemoPage() {
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [token, setToken] = useState<string | null>(null)
  const [coverage, setCoverage] = useState<any>(null)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [restored, setRestored] = useState(false)
  const saveTimer = useRef<any>(null)

  // Restore whatever the visitor had last time. Local only — this costs
  // nothing and is why closing the tab is not punished.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed.draft) {
          setDraft({ ...EMPTY, ...parsed.draft })
          // Answers come back; the position does not. Restoring the saved
          // step meant anyone who had previously reached the contact page —
          // including someone arriving fresh from a blog link months later —
          // landed on "give us your details" as the first thing they saw,
          // with no idea what they were signing up for. Progress is the
          // answers, not the page number, and re-clicking Continue over
          // pre-filled fields costs seconds.
          setRestored(Object.values(parsed.draft).some(
            (v: any) => Array.isArray(v) ? v.length > 0 : !!v))
        }
        if (parsed.token) setToken(parsed.token)
      }
    } catch { /* a corrupt draft must never block the form */ }
    demoApi.kbCoverage().then(r => setCoverage(r.data)).catch(() => setCoverage(null))
  }, [])

  const persistLocal = useCallback((next: Draft, _step: number, tok: string | null) => {
    try {
      // `step` is deliberately not stored. See the restore path above: a saved
      // position turns a fresh visit into a confusing one, and the answers are
      // the part worth keeping.
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ draft: next, token: tok }))
    } catch { /* private browsing — the form still works, it just won't resume */ }
  }, [])

  /**
   * Push the non-personal answers to the server, debounced.
   *
   * Contact fields are deliberately absent from this payload. They are
   * personal data and belong to the submit, not to a background save the
   * visitor did not ask for.
   */
  const syncDraft = useCallback((next: Draft) => {
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      const payload = {
        frameworks: next.frameworks,
        adoption_stage: next.adoption_stage || null,
        hosting: next.hosting || null,
        agent_count_band: next.agent_count_band || null,
        use_case_count_band: next.use_case_count_band || null,
        release_cadence: next.release_cadence || null,
        interested_features: next.interested_features,
      }
      try {
        let tok = token
        if (!tok) {
          const res = await demoApi.createDraft()
          tok = res.data?.draft_token
          setToken(tok)
          persistLocal(next, step, tok)
        }
        if (tok) await demoApi.saveDraft(tok, payload)
      } catch { /* the local copy is authoritative for the visitor */ }
    }, 800)
  }, [token, step, persistLocal])

  const update = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch }
    setDraft(next)
    persistLocal(next, step, token)
    if (!('name' in patch || 'email' in patch || 'company' in patch ||
          'job_title' in patch || 'notes' in patch)) {
      syncDraft(next)
    }
  }

  const toggle = (key: 'frameworks' | 'interested_features', id: string) => {
    const list = draft[key]
    update({ [key]: list.includes(id) ? list.filter(x => x !== id) : [...list, id] } as any)
  }

  const goto = (n: number) => { setStep(n); persistLocal(draft, n, token) }

  // Real numbers, or nothing. Lead with universal + selected so a thinly
  // covered framework never renders as "we barely support you".
  const applicableRules = (() => {
    if (!coverage?.by_framework) return null
    const universal = coverage.universal || 0
    const specific = draft.frameworks
      .filter(f => f !== 'other')
      .reduce((sum, f) => sum + (coverage.by_framework[f] || 0), 0)
    return universal + specific
  })()

  const submit = async () => {
    setError('')
    if (!draft.name.trim() || !draft.email.trim() || !draft.company.trim()) {
      setError('Name, work email and company are required.')
      return
    }
    setSubmitting(true)
    try {
      await demoApi.submit({ ...draft, draft_token: token, website: honeypot })
      localStorage.removeItem(STORAGE_KEY)
      setSubmitted(true)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Something went wrong. Please email sales@tryarias.com.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', p: 4, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 56, mb: 2 }} />
        <Typography variant="h5" gutterBottom>Your demo is being scheduled</Typography>
        <Typography color="text.secondary" paragraph>
          We have what we need to build the session around your stack. Someone
          will be in touch to find a time.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Anything urgent: <a href="mailto:sales@tryarias.com">sales@tryarias.com</a>
        </Typography>
      </Box>
    )
  }

  const agenda = FEATURES.filter(f => draft.interested_features.includes(f.id))

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" gutterBottom>Build your demo</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Three short steps. Every answer shapes the session — we do not run a
        generic walkthrough.
      </Typography>

      {restored && step === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}
               action={<Button color="inherit" size="small" onClick={() => {
                 localStorage.removeItem(STORAGE_KEY)
                 setDraft(EMPTY); setToken(null); setRestored(false)
               }}>Start over</Button>}>
          We kept your earlier answers.
        </Alert>
      )}

      <LinearProgress variant="determinate" value={((step + 1) / 3) * 100} sx={{ mb: 3, height: 6, borderRadius: 3 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: 3 }}>
        <Card variant="outlined">
          <CardContent>
            {step === 0 && (
              <>
                <Typography variant="h6" gutterBottom>What are you building with?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select any that apply. This is what decides which parts of the
                  platform are worth your time.
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                  {FRAMEWORKS.map(f => (
                    <Chip
                      key={f.id}
                      label={f.label}
                      onClick={() => toggle('frameworks', f.id)}
                      color={draft.frameworks.includes(f.id) ? 'primary' : 'default'}
                      variant={draft.frameworks.includes(f.id) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              </>
            )}

            {step === 1 && (
              <>
                <Typography variant="h6" gutterBottom>Where is your agent programme today?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Most teams are in more than one of these at once — pick where the
                  bulk of the work sits.
                </Typography>

                <ToggleButtonGroup
                  exclusive size="small" value={draft.adoption_stage}
                  sx={{ flexWrap: 'wrap' }}
                  onChange={(_, v) => v && update({ adoption_stage: v })}
                >
                  {ADOPTION.map(a => <ToggleButton key={a.id} value={a.id}>{a.label}</ToggleButton>)}
                </ToggleButtonGroup>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
                  How many use cases are live or planned?
                </Typography>
                <ToggleButtonGroup
                  exclusive size="small" value={draft.use_case_count_band}
                  sx={{ flexWrap: 'wrap' }}
                  onChange={(_, v) => v && update({ use_case_count_band: v })}
                >
                  {USE_CASE_BANDS.map(b => <ToggleButton key={b} value={b}>{b}</ToggleButton>)}
                </ToggleButtonGroup>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
                  And roughly how many agents across them?
                </Typography>
                <ToggleButtonGroup
                  exclusive size="small" value={draft.agent_count_band}
                  sx={{ flexWrap: 'wrap' }}
                  onChange={(_, v) => v && update({ agent_count_band: v })}
                >
                  {BANDS.map(b => <ToggleButton key={b} value={b}>{b}</ToggleButton>)}
                </ToggleButtonGroup>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
                  How often do those agents change?
                </Typography>
                <ToggleButtonGroup
                  exclusive size="small" value={draft.release_cadence}
                  sx={{ flexWrap: 'wrap' }}
                  onChange={(_, v) => v && update({ release_cadence: v })}
                >
                  {CADENCE.map(c => <ToggleButton key={c.id} value={c.id}>{c.label}</ToggleButton>)}
                </ToggleButtonGroup>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>Hosting</Typography>
                <ToggleButtonGroup
                  exclusive size="small" value={draft.hosting}
                  sx={{ flexWrap: 'wrap' }}
                  onChange={(_, v) => v && update({ hosting: v })}
                >
                  {HOSTING.map(h => <ToggleButton key={h.id} value={h.id}>{h.label}</ToggleButton>)}
                </ToggleButtonGroup>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 4 }}>
                  What should we spend the time on?
                </Typography>
                {FEATURE_GROUPS.map(g => (
                  <Box key={g.group} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block', mb: 1, letterSpacing: '.06em' }}>
                      {g.group.toUpperCase()}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                      {g.items.map(f => (
                        <Chip
                          key={f.id} label={f.label}
                          onClick={() => toggle('interested_features', f.id)}
                          color={draft.interested_features.includes(f.id) ? 'primary' : 'default'}
                          variant={draft.interested_features.includes(f.id) ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </>
            )}

            {step === 2 && (
              <>
                <Typography variant="h6" gutterBottom>Where should we send it?</Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <TextField label="Name" required value={draft.name} size="small"
                             onChange={e => update({ name: e.target.value })} />
                  <TextField label="Work email" required type="email" value={draft.email} size="small"
                             onChange={e => update({ email: e.target.value })} />
                  <TextField label="Company" required value={draft.company} size="small"
                             onChange={e => update({ company: e.target.value })} />
                  <TextField label="Job title" value={draft.job_title} size="small"
                             onChange={e => update({ job_title: e.target.value })} />
                  <TextField label="Anything specific you want to see?" multiline rows={3}
                             value={draft.notes} size="small"
                             onChange={e => update({ notes: e.target.value })} />
                  {/* Honeypot — hidden from people, irresistible to bots. */}
                  <input
                    type="text" name="website" tabIndex={-1} autoComplete="off"
                    value={honeypot} onChange={e => setHoneypot(e.target.value)}
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
                    aria-hidden="true"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Your contact details are sent only when you submit.
                </Typography>
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              </>
            )}

            <Divider sx={{ my: 3 }} />
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button disabled={step === 0} onClick={() => goto(step - 1)} startIcon={<ArrowBack />}>
                Back
              </Button>
              {step < 2 ? (
                <Button variant="contained" onClick={() => goto(step + 1)} endIcon={<ArrowForward />}>
                  Continue
                </Button>
              ) : (
                <Button variant="contained" onClick={submit} disabled={submitting}>
                  {submitting ? 'Sending…' : 'Request demo'}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* The payoff panel. Everything in here is either a real KB count or a
            statement of what ARIAS looks for. Never a simulated result. */}
        <Card variant="outlined" sx={{ position: { md: 'sticky' }, top: 24, alignSelf: 'start' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              YOUR DEMO SO FAR
            </Typography>

            {applicableRules !== null && (
              <Box sx={{ my: 2 }}>
                <Typography variant="h4">{applicableRules.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">
                  knowledge base rules apply to your stack
                  {draft.frameworks.length === 0 && ' before you pick a framework'}
                </Typography>
              </Box>
            )}

            {draft.frameworks.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  What we look for in your stack
                </Typography>
                {draft.frameworks.map(f => FRAMEWORK_INSIGHT[f] && (
                  <Typography key={f} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    • {FRAMEWORK_INSIGHT[f]}
                  </Typography>
                ))}
              </Box>
            )}

            {/* A general statement about how drift accumulates, keyed off their
                own cadence. Says nothing about their agents — we have not seen
                them — but makes the continuous argument concrete rather than
                asserted. */}
            {CADENCE_NOTE[draft.release_cadence] && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Why this matters at your pace
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {CADENCE_NOTE[draft.release_cadence]}
                </Typography>
              </Box>
            )}

            {agenda.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Your session will cover
                </Typography>
                {agenda.map(f => (
                  <Typography key={f.id} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    • {f.agenda}
                  </Typography>
                ))}
              </Box>
            )}

            {draft.frameworks.length === 0 && agenda.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Pick your frameworks and this fills in.
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Your progress is saved as you go, so you can come back to this.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
